import { useState } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, XCircle } from "lucide-react";
import { adminApi } from "@/lib/api";
import { Empty, RoleBadge, RoleSelect, StatusBadge, fmtDate } from "./TeamBits";

const input = "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40";

function InviteForm({ onSent }) {
  const [form, setForm] = useState({ email: "", name: "", role: "team_member", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.invite({ ...form, app_url: window.location.origin });
      toast.success(`Invitation sent to ${form.email}`);
      setForm({ email: "", name: "", role: "team_member", message: "" });
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invitation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6" data-testid="invite-form">
      <h3 className="font-serif text-xl font-semibold">Send team invitation</h3>
      <p className="mt-1 text-sm text-muted-foreground">They'll get an email link (valid 48 hours). Signing in with Google using this email activates their access instantly.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={input} data-testid="invite-email-input" />
        <input placeholder="Name (optional)" value={form.name} onChange={set("name")} className={input} data-testid="invite-name-input" />
        <RoleSelect value={form.role} onChange={set("role")} testId="invite-role-select" />
        <input placeholder="Personal note (optional)" value={form.message} onChange={set("message")} className={input} data-testid="invite-message-input" />
      </div>
      <button disabled={busy} data-testid="invite-submit-btn" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
        <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send invitation"}
      </button>
    </form>
  );
}

function InviteRow({ inv, onChange }) {
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  return (
    <div data-testid={`invite-${inv.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-4">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-semibold">{inv.name || inv.email} <RoleBadge role={inv.role} /></p>
        <p className="truncate text-sm text-muted-foreground">{inv.email} · invited by {inv.invited_by_name || inv.invited_by_email}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">Sent {fmtDate(inv.created_at)} · expires {fmtDate(inv.expires_at)}</p>
      </div>
      <StatusBadge status={inv.status} testId={`invite-status-${inv.id}`} />
      {inv.status !== "accepted" && (
        <button onClick={() => run(() => adminApi.resendInvite(inv.id, window.location.origin), "Invitation resent")} data-testid={`invite-resend-${inv.id}`} className="inline-flex items-center gap-1 rounded-xl border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary">
          <RefreshCw className="h-4 w-4" /> Resend
        </button>
      )}
      {inv.status === "pending" && (
        <button onClick={() => run(() => adminApi.revokeInvite(inv.id), "Invitation revoked")} data-testid={`invite-revoke-${inv.id}`} className="inline-flex items-center gap-1 rounded-xl border border-border px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10">
          <XCircle className="h-4 w-4" /> Revoke
        </button>
      )}
    </div>
  );
}

export function Invitations({ invites, onChange }) {
  return (
    <div className="space-y-6">
      <InviteForm onSent={onChange} />
      <div className="space-y-3" data-testid="invite-list">
        {invites.length ? invites.map((i) => <InviteRow key={i.id} inv={i} onChange={onChange} />) : <Empty>No invitations sent yet.</Empty>}
      </div>
    </div>
  );
}
